// @ts-check
// ── Cost and value of a client; Refunds and failed payments ──────────────────
//
// Part 4 of docs/superpowers/plans/2026-10-14-dashboard-build.md, from the approved prototype
// (mockups/ultimate-dashboard-v3-2026-10-13.html: the value and refund cards, cell, stat, miniLines, ltvChart,
// refundChart, bindRowTips), organic view: the coach runs no ads, so Cost to win a client waits for ad spend. The server
// (coach-dashboard.js `value`, _shared/dashboard/value.js) works every number out; this only says them.
//
// His rulings (10/14): every box is a label, the number alone and one short line; the working-out is in a hover that
// opens on keyboard focus too, read top to bottom as a short sum. Failed payments name the clients who owe, each a
// button to their profile (My Tasks has no failed payments to point to).
//
// THE PAID CARD (ad data plan Step 4, docs/superpowers/specs/2026-10-27-meta-ad-account-design.md "Dashboard"): once
// his Facebook and Instagram ad account is connected, the server's `ads` (_shared/dashboard/ads.js) turns the card into
// the prototype's `paid` branch: Lifetime value, Cost to win, Value to cost, Return on ad spend (the prototype's Cash back per $1), the Cost to win a client
// by month line, and a chip that says when the ad numbers last came in, or the day they stopped. Before he connects,
// only an account the ads switch lets in (`adsAvailable`) gets a button to the Settings card; every other coach's card
// is unchanged.

import { money, bigMoney, plural, monthOf, monthYear, longDay, tickMoney } from './words.js';
import { info, status, ICON } from './tiles.js';
import { escHtml } from '../utils.js';

const has = (/** @type {unknown} */ v) => v != null;
/** 'Oct' from '2026-10' @param {string} ym */
const short = (ym) => monthOf(`${ym}-01`).slice(0, 3);
/** 'October' from '2026-10' @param {string} ym */
const full = (ym) => monthOf(`${ym}-01`);
/** The same numbers as a table, for screen readers. @param {string} caption @param {string[][]} rows */
const table = (caption, rows) => `<div class="dv-sr"><table><caption>${escHtml(caption)}</caption><tbody>${rows.map((r) => `<tr><th scope="row">${escHtml(r[0])}</th><td>${escHtml(r[1])}</td></tr>`).join('')}</tbody></table></div>`;
/** A hover's rows, for a chart's column. @param {string} title @param {Array<[string, string, string]>} rows */
const tipRows = (title, rows) => `data-rows="${encodeURIComponent(JSON.stringify({ title, rows, total: null }))}"`;

/**
 * One box: label, the number alone (or what it is waiting for), one short line; its working-out in a hover.
 * @param {{ label: string, num: string|null, sub?: string, empty: string, wide?: boolean,
 *   tip?: { title: string, rows: string[][], total?: string[], note?: string }|null }} c
 */
function cell({ label, num, sub = '', empty, wide = false, tip = null }) {
  const withTip = num != null && tip;
  return `<div class="dv-cell${wide ? ' dv-cell--wide' : ''}${withTip ? ' dv-cell--tip' : ''}"${withTip ? ` tabindex="0" data-rowtip="${encodeURIComponent(JSON.stringify(tip))}"` : ''}>
      <span class="dv-label">${label}</span>
      <div class="dv-value">${num == null ? status('none', empty) : `<span class="dv-num">${num}</span>`}</div>
      ${sub ? `<div class="dv-sub">${sub}</div>` : ''}
    </div>`;
}

/** A stacked figure: label, number with its words, one line. @param {string} label @param {string} num @param {string} of @param {string} sub */
const stat = (label, num, of, sub) => `<div class="dv-stat">
      <span class="dv-label">${label}</span>
      <div class="dv-value"><span class="dv-num">${num}</span>${of ? `<span class="dv-of">${of}</span>` : ''}</div>
      ${sub ? `<div class="dv-sub">${sub}</div>` : ''}
    </div>`;

/** "A and B", "A, B and C" @param {string[]} parts */
const and = (parts) => (parts.length < 2 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

/** What a client pays back in months, as the prototype says it. @param {number} m */
const paysBack = (m) => (m < 1.5 ? '1 month' : `${m.toFixed(1)} months`);
/** Cents back for each $1, never rounded up to a dollar that was not reached. @param {number} n */
// Return on ad spend, floored to cents so 0.996 never reads as 1.00 ("Paid for itself"): big the way ads are read,
// "1.25x", and under it in dollars, "$1.25 back for every $1" (Clayton, 10/29)
const per1 = (n) => (Math.floor(Math.round(n * 10000) / 100) / 100).toFixed(2);
const roasX = (n) => `${per1(n)}x`;

/**
 * When the ad numbers last came in, in the coach's own day: "at 6:10 AM" today, "October 14 at 6:10 AM" before.
 * @param {string} at an ISO time @param {string|undefined} tz @param {string|undefined} today
 */
function updatedWords(at, tz, today) {
  const t = new Date(at);
  if (!Number.isFinite(t.getTime())) return '';
  const zone = tz ? { timeZone: tz } : {};
  const time = t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...zone }).replace(/\s+/g, ' ');
  const day = t.toLocaleDateString('en-CA', zone); // YYYY-MM-DD
  return day === today ? `, updated at ${time}` : `, updated ${longDay(day)} at ${time}`;
}

/**
 * The chip beside the card's title: the ads connected and when their numbers came in, or the day they stopped, with a
 * button to log in again (the Settings card says how).
 * @param {any} A the server's `ads` @param {string|undefined} tz @param {string|undefined} today
 */
function adsChip(A, tz, today) {
  if (A.stoppedOn) {
    return `<span class="dv-chip">${ICON.warn}<span><b>Facebook and Instagram ads</b> stopped updating on ${longDay(A.stoppedAt || A.stoppedOn)}</span>`
      + '<button type="button" class="dv-link" data-open-ads-card>Log in again</button></span>';
  }
  return `<span class="dv-chip">${ICON.good}<span><b>Facebook and Instagram ads</b> connected${A.updatedAt ? updatedWords(A.updatedAt, tz, today) : ''}</span></span>`;
}

/**
 * The paid card's four boxes and its line (the prototype's `paid` branch).
 * @param {any} V the server's `value` @param {any} A the server's `ads` @param {any} ltvCell
 */
function paidParts(V, A, ltvCell) {
  const C = A.cac, ok = C.paid != null, worth = ok && V.ltv != null && C.paid > 0;
  // Nothing spent in the 90 days: both boxes say so rather than wait for clients or say "from $0 in ads"
  const noSpend = !(C.spend > 0);
  const waiting = noSpend ? 'No ad spend in the last 90 days' : 'Shows once 5 clients come from ads';
  // Return on ad spend shows from the first client from ads (ads.js), so it waits for one, not five
  const waitingBack = noSpend ? 'No ad spend in the last 90 days' : 'Shows once a client comes from ads';
  const ratio = worth ? `${(V.ltv / C.paid).toFixed(1)} to 1` : '';
  const payback = worth && V.arpc ? C.paid / V.arpc : null;
  // The server works it out only once it means something (ads.js), so the box follows it
  const back = C.first != null;
  const cells = [
    ltvCell,
    cell({ label: 'Cost to win', num: ok ? bigMoney(C.paid) : null,
      sub: ok ? 'Per client from ads' : noSpend ? '' : `${C.clients ? plural(C.clients, 'client', 'clients') : 'No clients yet'} from ${money(C.spend)} in ads`,
      empty: waiting,
      tip: ok ? { title: 'Cost to win a client, last 90 days', rows: [['Spent on ads', money(C.spend)], ['Divided by clients from ads', String(C.clients)]], total: ['Cost to win each client', money(C.paid)],
        note: `Counting all ${C.allNew} new clients, ads or not, it's ${money(C.blended)} each.` } : null }),
    cell({ label: 'Value to cost', num: worth ? ratio : null, sub: payback != null ? `Pays back in ${paysBack(payback)}` : '', empty: 'Needs both numbers first',
      tip: worth ? { title: 'Value to cost', rows: [['Lifetime value', money(V.ltv)], ['Divided by cost to win', money(C.paid)]], total: ['Value to cost', ratio],
        ...(payback != null ? { note: `A client's first ${payback.toFixed(1)} months of pay cover what it cost to win them.` } : {}) } : null }),
    cell({ label: 'Return on ad spend', num: back ? roasX(C.first) : null,
      sub: back ? `${C.first < 0 ? '-$' : '$'}${per1(C.first).replace('-', '')} back for every $1<br>${C.first >= 1 ? status('good', 'Paid for itself') : status('warn', 'Not paid back yet')}` : '',
      empty: waitingBack,
      tip: back ? { title: 'Return on ad spend, clients\' first 30 days', rows: [['Paid by clients from ads in their first 30 days', money(C.first * C.spend)], ['Divided by ad spend', money(C.spend)]], total: ['Return on ad spend', roasX(C.first)],
        note: C.first >= 1 ? '1x or more means your ads paid for themselves within a month.' : 'Under 1x means your ads have not paid for themselves within a month yet.' } : null }),
  ];
  const any = /** @type {any[]} */ (A.cacByMonth).some((m) => has(m.fromAds));
  return {
    cells,
    title: 'Cost to win a client by month',
    key: any ? '<span class="dv-lkey"><span><i class="dv-lsw"></i>From ads</span><span><i class="dv-lsw dv-lsw--2"></i>All new clients</span></span>' : '',
    line: any ? '<div class="dv-plotbox dv-mini dv-fill" data-chart="cac"></div>'
      // 5 clients from ads but nothing spent: the line has no point to draw, and not for want of clients
      : `<div class="dv-empty dv-empty--short">${status('none', noSpend && C.clients >= 5 ? 'No ad spend in the last 90 days' : 'This line starts once 5 clients have come from your ads in 90 days')}</div>`,
    note: `Each point covers the 90 days up to the end of that month, so one slow month does not swing the line.${A.adsFrom ? ` Your ads started in ${monthYear(A.adsFrom)}.` : ''}`,
  };
}

/**
 * The row: Cost and value of a client (8 of 12 columns) and Refunds and failed payments (4).
 * @param {any} V the server's `value`
 * @param {any} [A] the server's `ads`: null until an ad account is connected
 * @param {{ canConnect?: boolean, tz?: string, today?: string }} [opts] canConnect: this account may connect one
 *   (the server's `adsAvailable`); tz and today: the coach's, for when the ad numbers came in
 */
export function valueRowHtml(V, A = null, { canConnect = false, tz, today } = {}) {
  const ltvTip = V.ltv == null ? null : {
    title: 'Lifetime value',
    rows: [['Each client pays you a month', money(V.arpc)], ['Times the months a client stays', String(V.months)]],
    total: ['Lifetime value', money(V.ltv)],
    note: `Monthly pay is from the last 12 months. Months stayed is from the ${V.left} clients who left in the last 2 years, past clients from your Stripe history included.`,
  };
  const F = V.firstCash;
  const ltvCell = cell({ label: 'Lifetime value', num: V.ltv == null ? null : bigMoney(V.ltv), sub: V.ltv == null ? 'Fills in after 10 clients have finished with you' : 'Per client', empty: 'Not enough history yet', tip: ltvTip });
  const paid = A ? paidParts(V, A, ltvCell) : null;
  const cells = paid ? paid.cells : [
    ltvCell,
    cell({ label: 'First-month cash', num: F.value == null ? null : bigMoney(F.value), sub: F.value == null ? '' : 'Per new client', empty: 'Shows once you have 5 new clients',
      tip: F.value == null ? null : { title: 'First-month cash, last 90 days', rows: [['Paid by new clients in their first 30 days', money(F.sum)], ['Divided by new clients', String(F.clients)]], total: ['Each new client', money(F.value)] } }),
    // ⚠️ Only an account the ads switch lets in is offered the connect button (the platform owner until Meta reviews the app, Step 8)
    cell({ label: 'Cost to win a client', num: null, sub: canConnect ? '<button type="button" class="dv-link" data-open-ads-card>Connect your Facebook and Instagram ads</button> to see this.' : 'Shows here once you run ads and FitOps has what you spend.', empty: 'No ad spend yet', wide: true }),
  ];
  const line = paid ? paid.line : /** @type {any[]} */ (V.ltvByMonth).some((m) => has(m.value))
    ? '<div class="dv-plotbox dv-mini dv-fill" data-chart="ltv"></div>'
    : `<div class="dv-empty dv-empty--short">${status('none', 'This line starts once 10 clients have finished with you')}</div>`;
  const valueCard = `<section class="dv-card dv-s8" aria-labelledby="dv-va-h">
      <div class="dv-card-head"><div class="dv-head-l"><h2 class="dv-title" id="dv-va-h">Cost and value of a client</h2>${info('value')}</div>${A ? adsChip(A, tz, today) : ''}</div>
      <div class="dv-cells">${cells.join('')}</div>
      <div class="dv-subhead"><b>${paid ? paid.title : 'Lifetime value by month'}</b>${paid ? paid.key : ''}</div>
      ${line}
      <div class="dv-chart-note">${paid ? paid.note : 'Worked out again at the end of each month.'}</div>
    </section>`;

  // ── Refunds and failed payments ──
  const R = V.refunds, Fl = V.failed, RC = V.recovered;
  const n = R.refunds + R.disputes;
  const what = and([R.refunds ? plural(R.refunds, 'refund', 'refunds') : '', R.disputes ? plural(R.disputes, 'dispute', 'disputes') : ''].filter(Boolean));
  const only = R.onlyMonth
    ? R.onlyMonth.count === 1
      ? ` The only one in the last 6 months was ${money(R.onlyMonth.amount)} in ${full(R.onlyMonth.month)}.`
      : ` The only ones in the last 6 months were in ${full(R.onlyMonth.month)}: ${money(R.onlyMonth.amount)}.`
    : '';
  const refundSub = n ? `${R.disputes ? 'Refunds and disputes were' : 'Refunds were'} ${R.pct90}% of your cash over the last 90 days` : `No refunds in the last 30 days.${only}`;
  const names = /** @type {Array<{ id: string, name: string }>} */ (Fl.clients);
  const people = names.slice(0, 3).map((c) => `<button type="button" class="dv-link" data-client="${escHtml(c.id)}">${escHtml(c.name)}</button>`);
  const owing = names.length ? `Who owes: ${and(names.length > 3 ? [...people, `${names.length - 3} more`] : people)}.` : '';
  const failSub = Fl.n
    ? `${Fl.retrying ? `${plural(Fl.retrying, 'payment', 'payments')} will be tried again. ` : ''}${Fl.needsCard ? `${plural(Fl.needsCard, 'payment needs', 'payments need')} a new card. ` : ''}${owing}`
    : 'Nothing unpaid right now';
  const stats = [
    stat('Refunded in the last 30 days', bigMoney(R.amount), n ? what : '', refundSub),
    stat('Failed payments', String(Fl.n), Fl.n ? `unpaid, ${money(Fl.amount)} in total` : '', failSub),
    stat('Recovered', bigMoney(RC.amount), RC.n ? `from ${plural(RC.n, 'payment', 'payments')}` : '', RC.n ? 'Failed payments that went through later, in the last 30 days' : 'Nothing recovered in the last 30 days'),
  ];
  const months = /** @type {any[]} */ (R.byMonth).filter((m) => m.amount > 0).length;
  const refundCard = `<section class="dv-card dv-s4" aria-labelledby="dv-rf-h">
      <div class="dv-card-head"><div class="dv-head-l"><h2 class="dv-title" id="dv-rf-h">Refunds and failed payments</h2>${info('refunds')}</div></div>
      <div class="dv-stats">${stats.join('')}</div>
      ${months >= 2 ? '<div class="dv-trend"><div class="dv-subhead"><b>Refunded each month</b></div><div class="dv-plotbox dv-mini" data-chart="refunds"></div></div>' : ''}
    </section>`;
  return `<div class="dv-grid dv-value-row">${valueCard}${refundCard}</div>`;
}

/**
 * Lifetime value at each of the last 12 month ends: a small line, no axis, the newest value printed, a hover per
 * month; a month with too little history breaks the line, so it never draws straight across a gap.
 * @param {Array<{ month: string, value: number|null }>} months @param {number} W @param {number} HB the box's free height
 */
export function ltvChart(months, W, HB) {
  const v = months.map((m) => m.value), last = v.length - 1;
  const h = Math.round(Math.max(116, Math.min(420, HB - 4))), top = 18, base = h - 24, slot = W / v.length;
  const xc = (/** @type {number} */ i) => slot * i + slot / 2;
  const real = /** @type {number[]} */ (v.filter(has));
  const hi = Math.max(...real), lo = Math.min(...real), pad = (hi - lo) * 0.3 || hi * 0.15 || 1;
  const y = (/** @type {number} */ n) => base - ((base - top) * (n - (lo - pad))) / (hi + pad - (lo - pad));
  const first = v.findIndex(has);
  const label = `Lifetime value by month: ${money(/** @type {number} */ (v[first]))} in ${short(months[first].month)}${has(v[last]) ? `, ${money(/** @type {number} */ (v[last]))} now` : ''}.`;
  let s = `<svg viewBox="0 0 ${W} ${h}" class="dv-plot" tabindex="0" role="img" aria-label="${escHtml(label)}">
    <defs><linearGradient id="dv-fade-line" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="var(--primary)" style="stop-opacity:var(--dv-fade-line)"/><stop offset="1" stop-color="var(--primary)" stop-opacity="0"/></linearGradient></defs>
    <line class="base" x1="0" x2="${W}" y1="${base}" y2="${base}"/>`;
  months.forEach((m, i) => { if (slot >= 34 || i % 2 === last % 2) s += `<text x="${xc(i)}" y="${h - 6}" text-anchor="middle">${short(m.month)}</text>`; });
  /** @type {Array<Array<[number, number]>>} */
  const runs = [];
  v.forEach((n, i) => { if (!has(n)) return; if (!runs.length || !has(v[i - 1])) runs.push([]); runs[runs.length - 1].push([i, /** @type {number} */ (n)]); });
  runs.forEach((pts) => {
    // ⚠️ A month with no neighbour has no line to draw: it is a dot, or it would not show at all (final review, 10/15)
    if (pts.length === 1 && pts[0][0] !== last) { s += `<circle class="dv-end" cx="${xc(pts[0][0])}" cy="${y(pts[0][1])}" r="4"/>`; return; }
    const d = pts.map((p) => `${xc(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(' L');
    s += `<path fill="url(#dv-fade-line)" d="M${xc(pts[0][0]).toFixed(1)},${base} L${d} L${xc(pts[pts.length - 1][0]).toFixed(1)},${base} Z"/><path class="ln" d="M${d}"/>`;
  });
  v.forEach((n, i) => {
    if (!has(n)) return;
    s += `<g class="col" data-i="${i}" ${tipRows(i === last ? `${full(months[i].month)}, worked out today` : full(months[i].month), [['Lifetime value', money(/** @type {number} */ (n)), 'line']])}>`
      + `<line class="guide" x1="${xc(i)}" x2="${xc(i)}" y1="${top}" y2="${base}"/><circle class="pt" cx="${xc(i)}" cy="${y(/** @type {number} */ (n))}" r="4"/>`
      + `<rect class="hit" x="${slot * i}" y="0" width="${slot}" height="${base}"/></g>`;
  });
  if (has(v[last])) {
    const e = /** @type {number} */ (v[last]), prev = v[last - 1], up = has(prev) ? Math.min(y(e), y(/** @type {number} */ (prev))) : y(e);
    s += `<circle class="dv-end" cx="${xc(last)}" cy="${y(e)}" r="4"/><text class="val" x="${xc(last) - 10}" y="${up - 9}" text-anchor="end">${money(e)}</text>`;
  }
  return s + '</svg>' + table('Lifetime value by month', months.map((m) => [full(m.month), has(m.value) ? money(/** @type {number} */ (m.value)) : 'Not enough history']));
}

/**
 * Cost to win a client at each of the last 12 month ends, each the 90 days before it: from ads (the line) and across
 * all new clients (dashed), drawn like ltvChart on one scale; a month without 5 clients from ads breaks both lines.
 * @param {Array<{ month: string, end: string, fromAds: number|null, all: number|null }>} months
 * @param {number} W @param {number} HB the box's free height @param {string|null} [adsFrom] the first day with spend
 */
export function cacChart(months, W, HB, adsFrom = null) {
  const p = months.map((m) => m.fromAds), b = months.map((m) => m.all), last = p.length - 1;
  const h = Math.round(Math.max(116, Math.min(420, HB - 4))), top = 18, base = h - 24, slot = W / p.length;
  const xc = (/** @type {number} */ i) => slot * i + slot / 2;
  const real = /** @type {number[]} */ ([...p, ...b].filter(has));
  const hi = Math.max(...real), lo = Math.min(...real), pad = (hi - lo) * 0.3 || hi * 0.15 || 1;
  const y = (/** @type {number} */ n) => base - ((base - top) * (n - (lo - pad))) / (hi + pad - (lo - pad));
  const first = p.findIndex(has);
  const label = `Cost to win a client by month, each point the 90 days before it. From ads: ${money(/** @type {number} */ (p[first]))} in ${short(months[first].month)}${has(p[last]) ? `, ${money(/** @type {number} */ (p[last]))} now` : ''}.${has(b[last]) ? ` All new clients: ${money(/** @type {number} */ (b[last]))} now.` : ''}`;
  let s = `<svg viewBox="0 0 ${W} ${h}" class="dv-plot" tabindex="0" role="img" aria-label="${escHtml(label)}">
    <defs><linearGradient id="dv-fade-cac" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="var(--primary)" style="stop-opacity:var(--dv-fade-line)"/><stop offset="1" stop-color="var(--primary)" stop-opacity="0"/></linearGradient></defs>
    <line class="base" x1="0" x2="${W}" y1="${base}" y2="${base}"/>`;
  months.forEach((m, i) => { if (slot >= 34 || i % 2 === last % 2) s += `<text x="${xc(i)}" y="${h - 6}" text-anchor="middle">${short(m.month)}</text>`; });
  // All new clients first, so the line from ads draws over it
  [{ v: b, two: true }, { v: p, two: false }].forEach(({ v, two }) => {
    /** @type {Array<Array<[number, number]>>} */
    const runs = [];
    v.forEach((n, i) => { if (!has(n)) return; if (!runs.length || !has(v[i - 1])) runs.push([]); runs[runs.length - 1].push([i, /** @type {number} */ (n)]); });
    runs.forEach((pts) => {
      // A month with no neighbour is a dot, or it would not show at all (ltvChart)
      if (pts.length === 1 && pts[0][0] !== last) { s += `<circle class="dv-end${two ? ' dv-end-2' : ''}" cx="${xc(pts[0][0])}" cy="${y(pts[0][1])}" r="4"/>`; return; }
      const d = pts.map((q) => `${xc(q[0]).toFixed(1)},${y(q[1]).toFixed(1)}`).join(' L');
      if (!two) s += `<path fill="url(#dv-fade-cac)" d="M${xc(pts[0][0]).toFixed(1)},${base} L${d} L${xc(pts[pts.length - 1][0]).toFixed(1)},${base} Z"/>`;
      s += `<path class="ln${two ? ' ln-2' : ''}" d="M${d}"/>`;
    });
  });
  p.forEach((n, i) => {
    if (!has(n)) return;
    const rows = /** @type {Array<[string, string, string]>} */ ([['From ads', money(/** @type {number} */ (n)), 'line'], ...(has(b[i]) ? [['All new clients', money(/** @type {number} */ (b[i])), 'dash']] : [])]);
    s += `<g class="col" data-i="${i}" ${tipRows(`The 90 days to ${longDay(months[i].end)}`, rows)}>`
      + `<line class="guide" x1="${xc(i)}" x2="${xc(i)}" y1="${top}" y2="${base}"/>${has(b[i]) ? `<circle class="pt pt-2" cx="${xc(i)}" cy="${y(/** @type {number} */ (b[i]))}" r="4"/>` : ''}<circle class="pt" cx="${xc(i)}" cy="${y(/** @type {number} */ (n))}" r="4"/>`
      + `<rect class="hit" x="${slot * i}" y="0" width="${slot}" height="${base}"/></g>`;
  });
  if (has(p[last])) {
    const e = /** @type {number} */ (p[last]), prev = p[last - 1], up = has(prev) ? Math.min(y(e), y(/** @type {number} */ (prev))) : y(e);
    s += `<circle class="dv-end" cx="${xc(last)}" cy="${y(e)}" r="4"/><text class="val" x="${xc(last) - 10}" y="${up - 9}" text-anchor="end">${money(e)}</text>`;
  }
  // A month with no number: before the ads started, no ad spend in its 90 days, or fewer than 5 clients from ads in them
  const none = (/** @type {{ end: string, spend?: number|null }} */ m) => (!adsFrom || m.end < adsFrom ? 'No ads yet'
    : m.spend === 0 ? 'No ad spend in those 90 days' : 'Fewer than 5 clients from ads');
  return s + '</svg>' + table('Cost to win a client by month', months.map((m) => [full(m.month), has(m.fromAds) ? `${money(/** @type {number} */ (m.fromAds))} from ads, ${has(m.all) ? money(/** @type {number} */ (m.all)) : '$0'} across all new clients` : none(m)]));
}

/**
 * Money given back in each of the last 6 months (this one so far); shown only when 2 or more of them had any.
 * @param {Array<{ month: string, amount: number|null }>} months @param {number} W
 */
export function refundChart(months, W) {
  const v = months.map((m) => m.amount), last = v.length - 1;
  const h = 96, base = 72, top = 20, slot = W / v.length, bw = Math.min(24, slot * 0.5);
  const max = Math.max(1, .../** @type {number[]} */ (v.filter(has))), peak = v.indexOf(max);
  const y = (/** @type {number} */ n) => base - ((base - top) * n) / max;
  let s = `<svg viewBox="0 0 ${W} ${h}" class="dv-plot" tabindex="0" role="img" aria-label="${escHtml(`Refunded each month: ${months.map((m) => `${short(m.month)} ${has(m.amount) ? money(/** @type {number} */ (m.amount)) : 'before FitOps'}`).join(', ')}.`)}">
    <line class="base" x1="0" x2="${W}" y1="${base}" y2="${base}"/>`;
  months.forEach((m, i) => {
    const n = m.amount, bx = slot * i + (slot - bw) / 2;
    s += `<text x="${bx + bw / 2}" y="${h - 4}" text-anchor="middle">${short(m.month)}</text>`;
    if (!has(n)) return;
    const amount = /** @type {number} */ (n), t = y(amount);
    s += `<g class="col" data-i="${i}" ${tipRows(`${full(m.month)}${i === last ? ' so far' : ''}`, [['Refunded', money(amount), 'bar']])}>`;
    if (amount > 0) s += `<path class="bar" d="M${bx},${base} V${t + 3} Q${bx},${t} ${bx + 3},${t} H${bx + bw - 3} Q${bx + bw},${t} ${bx + bw},${t + 3} V${base} Z"/>`;
    s += `<rect class="hit" x="${slot * i}" y="0" width="${slot}" height="${base}"/></g>`;
    if (amount > 0 && (i === last || i === peak)) s += `<text class="val" x="${bx + bw / 2}" y="${t - 6}" text-anchor="middle">${tickMoney(amount)}</text>`;
  });
  return s + '</svg>' + table('Refunded each month', months.map((m) => [full(m.month), has(m.amount) ? money(/** @type {number} */ (m.amount)) : 'Before FitOps']));
}

/**
 * The boxes' working-out, on hover and on keyboard focus, and the buttons to the clients who owe.
 * @param {ParentNode|null} root @param {HTMLElement} tip
 */
export function bindValueRow(root, tip) {
  if (!root) return;
  root.querySelectorAll('[data-rowtip]').forEach((node) => {
    const el = /** @type {HTMLElement} */ (node);
    const show = () => {
      const R = JSON.parse(decodeURIComponent(el.dataset.rowtip || '%7B%7D'));
      const row = (/** @type {string[]} */ [l, v]) => `<span>${escHtml(l)}</span><em>${escHtml(v)}</em>`;
      tip.innerHTML = `<b class="dv-tip-head">${escHtml(R.title)}</b><div class="dv-tip-rows">${R.rows.map(row).join('')}${R.total ? `<i class="dv-tip-rule"></i><span>${escHtml(R.total[0])}</span><em>${escHtml(R.total[1])}</em>` : ''}</div>${R.note ? `<p class="dv-tip-note">${escHtml(R.note)}</p>` : ''}`;
      tip.dataset.for = '';
      tip.classList.add('dv-tip--calc', 'on');
      const r = el.getBoundingClientRect();
      tip.style.left = '0px';
      const tw = tip.offsetWidth, th = tip.offsetHeight;
      tip.style.left = Math.max(8, Math.min(window.innerWidth - tw - 8, r.left + r.width / 2 - tw / 2)) + 'px';
      tip.style.top = (r.top - th - 8 >= 8 ? r.top - th - 8 : r.bottom + 8) + 'px';
    };
    const hide = () => tip.classList.remove('on', 'dv-tip--calc');
    el.addEventListener('mouseenter', show); el.addEventListener('focus', show);
    el.addEventListener('mouseleave', hide); el.addEventListener('blur', hide);
  });
  root.querySelectorAll('[data-client]').forEach((node) => {
    const el = /** @type {HTMLElement} */ (node);
    el.addEventListener('click', () => {
      const open = /** @type {any} */ (window).showClientProfile;
      if (typeof open === 'function') open(el.dataset.client);
    });
  });
  // Connect, or log in again: the Facebook and Instagram ads card in Settings > Integrations, through the app's real
  // view switcher (there is no global showView), then its tab once Settings has drawn
  root.querySelectorAll('[data-open-ads-card]').forEach((node) => {
    node.addEventListener('click', () => {
      const nav = /** @type {any} */ (window)._navigation;
      if (nav && typeof nav.showView === 'function') nav.showView('settings');
      else /** @type {HTMLElement|null} */ (document.querySelector('.nav-btn[data-view="settings"]'))?.click();
      setTimeout(() => /** @type {HTMLElement|null} */ (document.querySelector('.settings-tab-btn[data-tab="integrations"]'))?.click(), 60);
    });
  });
}
